import type { CellContext, ColumnFiltersState } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import {
  BOOLEAN_FILTER_OPTIONS,
  type DataTableColumnMeta,
} from 'components/data-table';
import { EMPTY_CELL } from 'components/link-cell';
import type { TableFilter } from './types';

/**
 * Shared `meta` for the `valued` column on the Properties and Property Allocations
 * views (INS-12871). The cell shows Yes/No, so the inline filter offers Yes/No too
 * (the underlying value is a boolean). `type: 'boolean'` makes the filter a multi-select
 * whose option VALUES are the wire `'true'`/`'false'` and whose LABELS read Yes/No, so
 * users filter on what they see instead of typing `true`/`false`. `align: 'left'` pins the
 * column left — `type: 'boolean'` would otherwise auto-center it (`getAlignment`), moving
 * it from where it sat before this filter existed.
 *
 * Lives in a plain `.ts` module (no component exports) so sharing it doesn't trip
 * `react-refresh/only-export-components`, and so the Yes/No option values (the wire
 * contract) can't drift between the two views. `enableColumnFilter` is NOT set here: the
 * filter is eval-scoped, so each view gates it on `calculationId` (a `valued` filter with no
 * calculation 422s — `requires_eval_calc`).
 */
export const VALUED_FILTER_META: DataTableColumnMeta = {
  type: 'boolean',
  align: 'left',
  filterOptions: BOOLEAN_FILTER_OPTIONS,
};

/**
 * Shared cell renderer for the `valued` column: the boolean reads as Yes/No (and the
 * empty-cell dash when it's null/undefined). Paired with {@link VALUED_FILTER_META} so the
 * Properties and Property Allocations views render the column identically (INS-12871).
 *
 * @param {CellContext} info - The TanStack cell context (only `getValue` is read).
 * @return {ReactNode} `'Yes'` / `'No'`, or the empty-cell placeholder.
 */
export function valuedCell<TData>(
  info: CellContext<TData, unknown>
): ReactNode {
  const value = info.getValue<boolean>();
  if (value === true) {
    return 'Yes';
  }
  if (value === false) {
    return 'No';
  }
  return EMPTY_CELL;
}

/**
 * The default "Valued = Yes" filter the Properties view opens with on a run evaluation
 * (INS-12871). Two mirrored halves of ONE default — kept in sync (a spec asserts
 * `toTableFilters(DEFAULT_VALUED_COLUMN_FILTERS, …)` equals `DEFAULT_VALUED_TABLE_FILTERS`):
 *   - `DEFAULT_VALUED_COLUMN_FILTERS` seeds the DataTable's inline filter row via
 *     `initialTableState.columnFilters` — a "Yes" selection on the Valued multi-select. It's
 *     only VISIBLE once the user opens the filter row (`isColumnFilterActive` defaults to
 *     false), so the grid can open already filtered to valued rows with the chip behind the
 *     filter toggle.
 *   - `DEFAULT_VALUED_TABLE_FILTERS` seeds the server read (`useMasterDataView`'s wire
 *     `tableFilters`) — it IS the wire shape `toTableFilters` emits for the column filter
 *     above (`valued` passes through unchanged; `type: 'boolean'` ⇒ multiselect ⇒
 *     `{ attribute, selectedValues }`).
 * The table is server-driven, so both are needed: the chip reflects the active filter and the
 * wire seed makes the first fetch actually return only valued rows. Applied ONLY in calc mode
 * — `valued` is eval-scoped, so a `valued` filter in package/draft mode 422s; callers gate on
 * `calculationId`.
 */
export const DEFAULT_VALUED_COLUMN_FILTERS: ColumnFiltersState = [
  { id: 'valued', value: ['true'] },
];
export const DEFAULT_VALUED_TABLE_FILTERS: TableFilter[] = [
  { attribute: 'valued', selectedValues: ['true'] },
];
